import React, { useEffect, useState } from 'react';
import { MdInfoOutline, MdWarningAmber } from 'react-icons/md';
import { useFormContext } from '../../../context/FormContext';
import { SignupLayout } from '../../ui/SignupLayout';
import { Spinner } from 'reactstrap';
import { updateQuote, savePolicy, issuePolicy } from '../../services/QuoteService';
import { generateNmDocument } from '../../services/DocumentService';
import {
    authorizePayment,
    salePayment,
    capturePayment,
    voidAuthorization,
} from '../../services/PaymentService';
import Session from '../../auth/Session';

const CREDIT_CARD_FEE = 0.03;

/*
 * Payment flow (token-based, auth-then-capture):
 *
 *   1. AUTHORIZE  - place a hold using the single-use payment_token. No money moves.
 *   2. ISSUE      - issue the policy via NormanMax. - if issuance fails  -> VOID the auth (customer never charged) -> error.
 *   3. CAPTURE    - only now is the customer actually charged, and only because the policy exists.
 *   4. PERSIST    - save policy to DB, generate documents, final quote update.
 *
 * Payment data arrives via router state as a single-use token only. We never see, forward, or store raw card / bank details anywhere.
 */
export function ProcessingPayment({ history }) {
    const { formData, setFields } = useFormContext();

    const paymentToken = history.location?.state?.paymentToken || '';
    const paymentType = history.location?.state?.paymentType || '';

    // 'processing' | 'error'
    const [status, setStatus] = useState('processing');
    const [errorMessage, setErrorMessage] = useState('');
    // When true, the customer's card has been charged and they must NOT retry.
    const [charged, setCharged] = useState(false);

    useEffect(() => {
        let cancelled = false;

        // Guard: if we somehow landed here without a token, bounce back
        if (!paymentToken) {
            const returnRoute = paymentType === 'creditCard' ? '/signup/payment/card' : '/signup/payment/bank';
            history.replace(returnRoute, { paymentError: true, paymentType });
            return;
        }

        async function run() {
            // Compute the amount ONCE so the figure we authorize, capture, and save can never diverge.
            const selectedOption = formData.RateOptions?.find(
                (o) => o.trigger_type === formData.SelectedTriggerType
            );
            const basePremium = selectedOption?.total_amount_due || formData.SelectedTotalPremium || 0;
            const isCard = paymentType === 'creditCard';
            const amount = isCard
                ? Math.round(basePremium * (1 + CREDIT_CARD_FEE) * 100) / 100
                : basePremium;

            // Idempotency key generated on the payment-details screen and carried in formData.
            // Used as NMI orderid + our own dedupe key.
            const idempotencyKey = formData.PaymentIdempotencyKey || '';

            try {
                // The two payment rails differ at the gateway:
                //   CARD: authorize (hold) -> issue policy -> capture (or void)
                //   ACH:  issue policy -> sale   (ACH has no auth/capture split)
                // Both converge below at setCharged(true) + post-payment steps.
                let issuePolicyResult;

                if (isCard) {
                    // STEP 1: AUTHORIZE (hold only - no funds captured yet)
                    const auth = await authorizePayment({
                        paymentToken,
                        amount,
                        idempotencyKey,
                        formData,
                    });
                    if (cancelled) return;

                    if (!auth.success) {
                        // Auth declined/failed. Customer not charged.
                        // Send them back to re-enter payment details
                        history.push('/signup/payment/card', {
                            paymentError: true,
                            paymentErrorMessage: auth.errorMessage || '',
                            paymentType,
                        });
                        return;
                    }

                    const transactionId = auth.transactionId;

                    // STEP 2: ISSUE POLICY (money has NOT moved yet)
                    issuePolicyResult = await issuePolicy(buildIssuePolicyPayload(formData));
                    if (cancelled) return;

                    if (!issuePolicyResult.success) {
                        // Release the hold so the customer is never charged for a policy that does not exist.
                        try {
                            await voidAuthorization({ transactionId, idempotencyKey, formData });
                        } catch (voidErr) {
                            console.error('Failed to void authorization after issue failure:', voidErr);
                        }
                        setErrorMessage(
                            issuePolicyResult.error_message ||
                                'We were unable to issue your policy. You have not been charged.'
                        );
                        setStatus('error');
                        return;
                    }

                    // STEP 3: CAPTURE (the customer is charged NOW, and only because the policy was successfully issued).
                    const capture = await capturePayment({ transactionId, amount, idempotencyKey, formData });
                    if (cancelled) return;

                    if (!capture.success) {
                        // Policy issued but capture failed. 
                        setErrorMessage(
                            'Your policy was issued, but we had trouble finalizing the payment.'
                        );
                        setStatus('error');
                        return;
                    }
                } else {
                    // ACH: no hold/capture. Issue the policy FIRST, then run the sale.
                    // If issuance fails, nothing was sent to the gateway, so there is nothing to reverse.

                    // STEP 1: ISSUE POLICY (no money involved yet)
                    issuePolicyResult = await issuePolicy(buildIssuePolicyPayload(formData));
                    if (cancelled) return;

                    if (!issuePolicyResult.success) {
                        setErrorMessage(issuePolicyResult.error_message || 'We were unable to issue your policy. You have not been charged.');
                        setStatus('error');
                        return;
                    }

                    // STEP 2: ACH SALE (submitted only because the policy exists)
                    const sale = await salePayment({
                        paymentToken,
                        amount,
                        idempotencyKey,
                        formData,
                    });
                    if (cancelled) return;

                    if (!sale.success) {
                        // Policy issued but the ACH debit was rejected at submit
                        // (e.g. invalid routing/account).
                        setErrorMessage(
                            'Your policy was issued, but we had trouble processing your bank payment.'
                        );
                        setStatus('error');
                        return;
                    }
                }

                setCharged(true);

                // STEP 4: Persist policy to DB
                try {
                    await savePolicy(formData, {
                        ...issuePolicyResult,
                        total_premium: amount,
                    });
                } catch (saveErr) {
                    console.error('Failed to persist policy to DB:', saveErr);
                }

                // Generate DES document
                try {
                    const sessionToken = Session.getToken();
                    await generateDes(formData.QuoteId, sessionToken);
                } catch (desErr) {
                    console.error('Failed to generate DES document:', desErr);
                }

                // POLICY / INVOICE documents (fire-and-forget; server-side delay)
                if (formData.QuoteId) {
                    generateNmDocument(formData.QuoteId, 'POLICY', 60).catch((err) =>
                        console.error('Failed to generate policy document:', err)
                    );
                    generateNmDocument(formData.QuoteId, 'INVOICE', 10).catch((err) =>
                        console.error('Failed to generate invoice document:', err)
                    );
                }

                // Final quote update
                if (formData.QuoteId) {
                    try {
                        await updateQuote(formData.QuoteId, {
                            ...formData,
                            currentStep: 'complete',
                            PolicyNumber: issuePolicyResult.policy_number || '',
                            ExpiryDate: issuePolicyResult.expiry_date || '',
                        });
                    } catch (updateErr) {
                        console.error('Failed to update quote on policy issue:', updateErr);
                    }
                }

                setFields({
                    PolicyNumber: issuePolicyResult.policy_number || '',
                    ExpiryDate: issuePolicyResult.expiry_date || '',
                });

                history.push('/signup/complete');
            } catch (err) {
                if (cancelled) return;
                setErrorMessage(err.message || 'An unexpected error occurred while processing your payment.');
                setStatus('error');
            }
        }

        run();
        return () => {
            cancelled = true;
        };
    }, []);

    return (
        <SignupLayout showProgress={false}>
            {status === 'processing' && (
                <>
                    <h1 className="signup-heading" style={{ textAlign: 'center', marginTop: 0 }}>
                        Processing payment
                    </h1>
                    <p className="signup-section__desc" style={{ textAlign: 'center' }}>
                        We're securely verifying your payment details.
                    </p>

                    <div className="dp-loading" aria-label="Processing payment…">
                        <Spinner />
                    </div>

                    <div className="security-banner" style={{ borderColor: 'var(--brand-primary)' }}>
                        <MdInfoOutline size={20} className="security-banner__icon" />
                        <div className="security-banner__text">
                            <strong>Please don't close this window</strong>
                            <p>
                                This process may take up to <strong>30 seconds.</strong>
                            </p>
                        </div>
                    </div>
                </>
            )}

            {status === 'error' && (
                <div className="processing-error">
                    <div className="processing-error__banner">
                        <MdWarningAmber size={24} className="processing-error__icon" />
                        <strong>Something went wrong</strong>
                    </div>
                    <p className="processing-error__message">{errorMessage}</p>
                    <div className="processing-error__help">
                        {charged ? (
                            <>
                                <p>
                                    Your payment has been collected. Please do not attempt to
                                    resubmit — contact our team immediately and we'll get this
                                    resolved for you.
                                </p>
                                <p>
                                    Call us at{' '}
                                    <a href="tel:8772695422">
                                        <strong>877-269-5422</strong>
                                    </a>
                                </p>
                            </>
                        ) : (
                            <>
                                <p>
                                    You have not been charged. If you continue to have trouble,
                                    please contact our team and we'll be glad to help.
                                </p>
                                <p>
                                    Call us at{' '}
                                    <a href="tel:8772695422">
                                        <strong>877-269-5422</strong>
                                    </a>
                                </p>
                            </>
                        )}
                    </div>
                </div>
            )}
        </SignupLayout>
    );
}

// Build issuePolicy payload
function buildIssuePolicyPayload(formData) {
    return {
        quoteNumber: formData.QuoteNumber,
        selectedTriggerType: formData.SelectedTriggerType,
        producingAgent: {
            name: 'John Smith',
            emailAddress: 'john.smith@agency.com',
        },
        additionalParties:
            formData.HasAdditionalParties === true && formData.AdditionalPartyType !== ''
                ? [
                      {
                          type: formData.AdditionalPartyType || '',
                          name:
                              formData.AdditionalPartyName !== ''
                                  ? formData.AdditionalPartyName
                                  : [formData.AdditionalPartyFirstName, formData.AdditionalPartyLastName]
                                        .filter(Boolean)
                                        .join(' '),
                          address: formData.AdditionalPartyAddressOne || '',
                          city: formData.AdditionalPartyCity || '',
                          state: formData.AdditionalPartyState || '',
                          zipCode: formData.AdditionalPartyZipCode || '',
                          country: formData.AdditionalPartyCountry || 'US',
                      },
                  ]
                : null,
        mailingSameAsProperty: formData.MailingSameAsProperty,
        mailingAddressOne: formData.ContactAddressOne,
        mailingCity: formData.ContactCity,
        mailingState: formData.ContactState,
        mailingZipCode: formData.ContactZipCode,
        targetAddressOne: formData.TargetAddressOne,
        targetCity: formData.TargetCity,
        targetState: formData.TargetState,
        targetZipCode: formData.TargetZipCode,
    };
}

async function generateDes(quoteId, token) {
    if (!quoteId) return;

    const response = await fetch(`api/Document/GenerateDes/${quoteId}`, {
        method: 'POST',
        headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
    });

    if (!response.ok) {
        throw new Error(`GenerateDes failed: ${response.status}`);
    }

    return response.json();
}